import request from "@/lib/api/handler";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useProfileQuery } from "@/queries/user/profile";

import { QueryKeys } from "@/queries/utils";
import { Candidate } from "@/queries/candidate/candidate";
import { IProject } from "@/redux/features/candidateProfileSlice";
import { toast } from "sonner";

export const useDeleteProject = (
  profileId: string,
  projectId: string,
  onSuccess?: () => void,
) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();
  const { profile } = useProfileQuery();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async () => {
      const token = await getAccessTokenSilently();

      return request<IProject>({
        method: "DELETE",
        path: `/api/candidate/${profileId || "foo"}/project/${projectId ?? ""}`,
        token,
      });
    },
    onSuccess: () => {
      const prevCandidate = qc.getQueryData<Candidate>(
        QueryKeys.candidate(profileId),
      );

      if (prevCandidate) {
        const updatedCandidate = {
          ...prevCandidate,
        };

        const projects = [...prevCandidate.projects];
        const prevProjectIdx = projects.findIndex(({ id }) => id === projectId);

        if (prevProjectIdx !== -1) {
          projects.splice(prevProjectIdx, 1);
        }
        updatedCandidate.projects = projects;

        qc.setQueryData<Candidate>(
          QueryKeys.candidate(profileId),
          updatedCandidate,
        );
      }

      if (profile?.id === profileId) {
        const prevSelf = qc.getQueryData<Candidate>(QueryKeys.myCandidate);
        if (prevSelf) {
          const updatedCandidate = {
            ...prevSelf,
          };

          const projects = [...prevSelf.projects];
          const prevProjectIdx = projects.findIndex(
            ({ id }) => id === projectId,
          );

          if (prevProjectIdx !== -1) {
            projects.splice(prevProjectIdx, 1);
          }
          updatedCandidate.projects = projects;

          qc.setQueryData<Candidate>(QueryKeys.myCandidate, updatedCandidate);
        }
      }

      onSuccess?.();
    },
    onError: () => {
      toast.error("Failed to delete project, please try again later.");
    },
  });

  return {
    deleteProject: mutate,
    isDeletingProject: isPending,
    deleteProjectError: error,
  };
};
